export interface RcaTextRow {
  label: string;
  value: string;
}

export interface RcaWindowRow {
  window: string;
  dateRange: string;
  signal: string;
  battery: string;
}

export interface RcaCustodyRow {
  stage: string;
  dateIn: string;
  dateOut: string;
  person: string;
  notes: string;
}

export interface RcaStepRow {
  subsection: string;
  action: string;
  result: string;
}

export interface RcaEditorialRow {
  name: string;
  date: string;
  notes: string;
}

export interface RcaReport {
  reportDate: string;
  customer: string;
  dateReceived: string;
  customerContact: string;
  phone: string;
  model: string;
  rep: string;
  eventDescription: string;
  analysis: string;
  correctiveActions: string;
  signedBy: string;
  signedDate: string;
}

export interface RcaBattery {
  type: string;
  initialVoltages: string;
  health: string;
}

export interface RcaDiagnostics {
  visualInspection: string;
  configDump: string;
  statDump: string;
  battDump: string;
  cellCommunicate: string;
  serialCommunicate: string;
}

export interface RcaDeviceWork {
  boardSku: string;
  dateProblemsFirstReported: string;
  fieldInstallDate: string;
  battery: RcaBattery;
  diagnostics: RcaDiagnostics;
  windows: RcaWindowRow[];
}

export interface RcaSignOff {
  name: string;
  date: string;
  avatarUrl: string;
}

export interface RcaFormState {
  devices: Record<string, RcaDeviceWork>;
  custody: RcaCustodyRow[];
  troubleshooting: RcaStepRow[];
  editorials: RcaEditorialRow[];
  statusAchievedAt: Partial<Record<RcaFlowStep, string>>;
  returnedForDispositionAt: string;
  conclusion: {
    rootCause: string;
    evidence: string;
    openQuestions: string;
    correctiveActions: string;
    signOff: RcaSignOff;
  };
  draft: RcaReport;
  final: RcaReport;
}

export const RCA_LOG_WINDOWS = [
  "Week leading up to shipping",
  "Week after shipping",
  "Week after field installation",
  "Week device began to have problems",
  "Week device arrived back at SGS",
  "Other notable log entries",
];

export const RCA_AUTOMATIC_CUSTODY_STAGES = ["Received at SGS (intake)", "Logged in RMA Database"] as const;

const RCA_REMOVED_CUSTODY_STAGES = new Set([
  "Visual inspection & photos",
  "Battery readings collected",
  "Config / stat / batt dumps captured",
  "Re-boxed and labeled with RMA #",
]);

export const RCA_FLOW_STEPS = [
  "Waiting for intake",
  "Received",
  "Intake complete",
  "Escalate to TenX",
  "Delivered to TenX",
  "RCA inspection complete",
  "Report created and edited",
  "Report sent to customer",
] as const;

export type RcaFlowStep = (typeof RCA_FLOW_STEPS)[number];

export const RCA_DISPOSITION_FLAG = "Returned to operations for disposition";

export const RCA_CUSTODY_STAGES = [
  "RCA decision",
  "Handed off to SGS engineering for RCA",
  "SGS engineering inspection complete",
  "RCA level 2 decision",
  "Handed off to TenX for level 2",
  "TenX inspection complete",
  "Returned to operations / disposition",
  "Report created and edited",
  "Report sent to customer",
];

export const RCA_TROUBLESHOOTING_ROWS = [
  "Suspected issue",
  "Hypothesis to test",
  "Step 1",
  "Step 2",
  "Step 3",
  "Step 4",
  "Step 5",
  "Confirmation test (e.g. re-run batt / stat)",
  "Batteries disconnected / replaced? Batt command output?",
  "Depassivation (Attempted / Method / Results / Batt Command Output)",
  "Power consumption measured? Within spec?",
  "Power consumption recheck",
];

export interface RcaFormPresentInput {
  serials: string[];
  report?: Partial<RcaReport>;
  firstDevice?: {
    initialVoltages?: string;
    visualInspection?: string;
  };
  statusSeeds?: Partial<Record<RcaFlowStep, string>>;
}

export function rcaDeviceKey(serial: string): string {
  return serial.trim().toUpperCase();
}

export function replaceDeviceWork(form: RcaFormState, serial: string, work: RcaDeviceWork): RcaFormState {
  const key = rcaDeviceKey(serial);
  return { ...form, devices: { ...form.devices, [key]: work } };
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function objectRecord(value: unknown): Record<string, unknown> | null {
  if (value == null || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function kept(stored: unknown, suggestion: string): string {
  if (typeof stored === "string") return stored;
  return suggestion;
}

function blankReport(): RcaReport {
  return {
    reportDate: "",
    customer: "",
    dateReceived: "",
    customerContact: "",
    phone: "",
    model: "",
    rep: "",
    eventDescription: "",
    analysis: "",
    correctiveActions: "",
    signedBy: "",
    signedDate: "",
  };
}

function presentReport(stored: unknown, seed: Partial<RcaReport>): RcaReport {
  const row = objectRecord(stored);
  const blank = blankReport();
  const next = { ...blank };
  for (const key of Object.keys(blank) as Array<keyof RcaReport>) {
    next[key] = kept(row?.[key], seed[key] ?? "");
  }
  return next;
}

function blankDiagnostics(): RcaDiagnostics {
  return {
    visualInspection: "",
    configDump: "",
    statDump: "",
    battDump: "",
    cellCommunicate: "",
    serialCommunicate: "",
  };
}

function presentBattery(stored: unknown, initialVoltages: string): RcaBattery {
  const row = objectRecord(stored);
  return {
    type: kept(row?.type, ""),
    initialVoltages: kept(row?.initialVoltages, initialVoltages),
    health: kept(row?.health, ""),
  };
}

function presentDiagnostics(stored: unknown, visualInspection: string): RcaDiagnostics {
  const row = objectRecord(stored);
  const blank = blankDiagnostics();
  return {
    visualInspection: kept(row?.visualInspection, visualInspection),
    configDump: kept(row?.configDump, blank.configDump),
    statDump: kept(row?.statDump, blank.statDump),
    battDump: kept(row?.battDump, blank.battDump),
    cellCommunicate: kept(row?.cellCommunicate, blank.cellCommunicate),
    serialCommunicate: kept(row?.serialCommunicate, blank.serialCommunicate),
  };
}

function presentDeviceWork(
  stored: unknown,
  legacy: { battery: unknown; diagnostics: unknown; windows: unknown; identification: Record<string, unknown> | null; voltages: string; visual: string } | null,
): RcaDeviceWork {
  const row = objectRecord(stored);
  return {
    boardSku: kept(row?.boardSku, ""),
    dateProblemsFirstReported: kept(row?.dateProblemsFirstReported, text(legacy?.identification?.dateProblemsFirstReported)),
    fieldInstallDate: kept(row?.fieldInstallDate, text(legacy?.identification?.fieldInstallDate)),
    battery: presentBattery(row?.battery ?? legacy?.battery, legacy?.voltages ?? ""),
    diagnostics: presentDiagnostics(row?.diagnostics ?? legacy?.diagnostics, legacy?.visual ?? ""),
    windows: presentWindows(row?.windows ?? legacy?.windows),
  };
}

/**
 * Device work stored on the NCR (quality.ncr_device_work.work). Until the NCR row is saved once,
 * the legacy entry from quality.rca.form.devices seeds every field.
 */
export function presentNcrDeviceWork(stored: unknown, seed: RcaDeviceWork | null): RcaDeviceWork {
  const row = objectRecord(stored);
  const hasStored = row != null && Object.keys(row).length > 0;
  if (!hasStored) {
    return seed ?? presentDeviceWork(null, null);
  }
  return {
    boardSku: kept(row.boardSku, seed?.boardSku ?? ""),
    dateProblemsFirstReported: kept(row.dateProblemsFirstReported, seed?.dateProblemsFirstReported ?? ""),
    fieldInstallDate: kept(row.fieldInstallDate, seed?.fieldInstallDate ?? ""),
    battery: presentBattery(row.battery ?? seed?.battery, seed?.battery.initialVoltages ?? ""),
    diagnostics: presentDiagnostics(row.diagnostics ?? seed?.diagnostics, seed?.diagnostics.visualInspection ?? ""),
    windows: presentWindows(row.windows ?? seed?.windows),
  };
}

export function presentRcaForm(stored: unknown, input: RcaFormPresentInput): RcaFormState {
  const root = objectRecord(stored);
  const deviceMap = objectRecord(root?.devices);
  const legacy = deviceMap == null;
  const identification = legacy ? objectRecord(root?.identification) : null;
  const conclusion = objectRecord(root?.conclusion);
  const serials = [...new Set(input.serials.map((serial) => rcaDeviceKey(serial)).filter((serial) => serial !== ""))];
  const devices: Record<string, RcaDeviceWork> = {};
  serials.forEach((serial, index) => {
    const saved = deviceMap?.[serial];
    const first = legacy && index === 0;
    devices[serial] = presentDeviceWork(
      saved,
      first
        ? {
            battery: root?.battery,
            diagnostics: root?.diagnostics,
            windows: root?.windows,
            identification,
            voltages: input.firstDevice?.initialVoltages ?? "",
            visual: input.firstDevice?.visualInspection ?? "",
          }
        : null,
    );
  });
  const custody = presentCustody(root?.custody);
  return {
    devices,
    custody,
    troubleshooting: presentSteps(root?.troubleshooting),
    editorials: presentEditorials(root?.editorials),
    statusAchievedAt: presentStatusAchievedAt(root?.statusAchievedAt, custody, input.statusSeeds),
    returnedForDispositionAt: presentDispositionAt(root?.returnedForDispositionAt, custody),
    conclusion: {
      rootCause: kept(conclusion?.rootCause, ""),
      evidence: kept(conclusion?.evidence, ""),
      openQuestions: kept(conclusion?.openQuestions, ""),
      correctiveActions: kept(conclusion?.correctiveActions, ""),
      signOff: presentSignOff(conclusion?.signOff),
    },
    draft: presentReport(root?.draft, input.report ?? {}),
    final: presentReport(root?.final, input.report ?? {}),
  };
}

function presentStatusAchievedAt(
  stored: unknown,
  custody: RcaCustodyRow[],
  seeds: Partial<Record<RcaFlowStep, string>> | undefined,
): Partial<Record<RcaFlowStep, string>> {
  const saved = objectRecord(stored);
  const marks: Partial<Record<RcaFlowStep, string>> = {};
  for (const step of RCA_FLOW_STEPS) {
    const savedAt = text(saved?.[step]).trim();
    if (savedAt !== "") {
      marks[step] = savedAt;
      continue;
    }
    const fromCustody = custodyDate(custody, CUSTODY_STEP_NAMES[step]);
    const seeded = seeds?.[step]?.trim() ?? "";
    const achievedAt = fromCustody || seeded;
    if (achievedAt !== "") marks[step] = achievedAt;
  }
  return marks;
}

function presentDispositionAt(stored: unknown, custody: RcaCustodyRow[]): string {
  const saved = text(stored).trim();
  if (saved !== "") return saved;
  return custodyDate(custody, ["Returned to operations / disposition", "Returned to operations for disposition"]);
}

const CUSTODY_STEP_NAMES: Record<RcaFlowStep, string[]> = {
  "Waiting for intake": [],
  Received: ["Received at SGS (intake)"],
  "Intake complete": ["Logged in RMA Database"],
  "Escalate to TenX": ["RCA decision", "RCA level 2 decision"],
  "Delivered to TenX": ["Handed off to TenX for level 2"],
  "RCA inspection complete": ["SGS engineering inspection complete", "TenX inspection complete"],
  "Report created and edited": ["Report created and edited"],
  "Report sent to customer": ["Report sent to customer"],
};

function custodyDate(custody: RcaCustodyRow[], names: string[]): string {
  for (const name of names) {
    const row = custody.find((item) => item.stage.trim() === name);
    const date = row?.dateIn.trim() || row?.dateOut.trim() || "";
    if (date !== "") return date;
  }
  return "";
}

function presentSignOff(stored: unknown): RcaSignOff {
  if (typeof stored === "string") {
    return { name: stored.trim(), date: "", avatarUrl: "" };
  }
  const row = objectRecord(stored);
  return {
    name: text(row?.name),
    date: text(row?.date),
    avatarUrl: text(row?.avatarUrl),
  };
}

function presentWindows(stored: unknown): RcaWindowRow[] {
  if (!Array.isArray(stored)) {
    return RCA_LOG_WINDOWS.map((window) => ({ window, dateRange: "", signal: "", battery: "" }));
  }
  return stored.map((row) => {
    const record = objectRecord(row);
    return {
      window: text(record?.window),
      dateRange: text(record?.dateRange),
      signal: text(record?.signal),
      battery: text(record?.battery),
    };
  });
}

function presentCustody(stored: unknown): RcaCustodyRow[] {
  if (!Array.isArray(stored)) {
    return RCA_CUSTODY_STAGES.map((stage) => ({
      stage,
      dateIn: "",
      dateOut: "",
      person: "",
      notes: "",
    }));
  }
  return stored.flatMap((row) => {
    const record = objectRecord(row);
    const stage = text(record?.stage);
    if (RCA_REMOVED_CUSTODY_STAGES.has(stage)) return [];
    return [{
      stage,
      dateIn: text(record?.dateIn),
      dateOut: text(record?.dateOut),
      person: text(record?.person),
      notes: text(record?.notes),
    }];
  });
}

function presentSteps(stored: unknown): RcaStepRow[] {
  if (!Array.isArray(stored)) {
    return RCA_TROUBLESHOOTING_ROWS.map((subsection) => ({ subsection, action: "", result: "" }));
  }
  return stored.map((row) => {
    const record = objectRecord(row);
    return {
      subsection: text(record?.subsection),
      action: text(record?.action),
      result: text(record?.result),
    };
  });
}

function presentEditorials(stored: unknown): RcaEditorialRow[] {
  if (!Array.isArray(stored) || stored.length === 0) {
    return [{ name: "", date: "", notes: "" }];
  }
  return stored.map((row) => {
    const record = objectRecord(row);
    return {
      name: text(record?.name),
      date: text(record?.date),
      notes: text(record?.notes),
    };
  });
}
